import React, { useEffect, useState } from 'react';
import { reportsAPI, orgAPI } from '@/services/api';
import ReportDialog from '@/components/shared/ReportDialog';
import { FileSpreadsheet, Download, AlertCircle } from 'lucide-react';

export default function RecruiterReports() {
  const [columns, setColumns] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, octx] = await Promise.all([reportsAPI.recruiterColumns(), orgAPI.getContext()]);
        setColumns(c.columns || []);
        setCtx(octx);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, []);

  const isHead = !!ctx?.is_head;
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);

  const filterSchema = [
    { key: 'start_date', label: 'Start date', type: 'date' },
    { key: 'end_date', label: 'End date', type: 'date' },
    { key: 'status', label: 'Status', type: 'select', options: [
      { value: 'active', label: 'Active' }, { value: 'archived', label: 'Archived' }, { value: 'closed', label: 'Closed' },
    ] },
    { key: 'job_type', label: 'Job type', type: 'select', options: [
      { value: 'permanent', label: 'Permanent' }, { value: 'contract', label: 'Contract' }, { value: 'part-time', label: 'Part-time' }, { value: 'internship', label: 'Internship' },
    ] },
    { key: 'min_matches', label: 'Min applicants matched', type: 'number', placeholder: 'e.g. 1' },
    { key: 'max_matches', label: 'Max applicants matched', type: 'number', placeholder: 'e.g. 50' },
    ...(isHead ? [{ key: 'recruiter_email', label: 'Posted by (email contains)', type: 'text', placeholder: 'name@…' }] : []),
  ];

  const defaultSelected = ['job_id', 'title', 'status', 'posted_by_email', 'created_at', 'total_matches'];
  const defaultFilters = { start_date: monthAgo, end_date: today };

  return (
    <div data-testid="recruiter-reports">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> Reports</h1>
        <p className="text-slate-500 dark:text-zinc-500 text-sm">
          {isHead ? 'Download a CSV of every job posted by your team within a date range. Pick filters, columns, preview, then download.' :
            'Download a CSV of your own job postings. Pick filters, columns, preview, then download.'}
        </p>
      </div>

      {error && <div className="mb-4 px-4 py-2 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center"><FileSpreadsheet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /></div>
            <div>
              <h2 className="text-slate-900 dark:text-white font-medium">Jobs activity report</h2>
              <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">Every job posting in the chosen window with match counts and outcomes.</p>
            </div>
          </div>
          <ul className="text-slate-500 dark:text-zinc-400 text-xs space-y-1.5 mb-5 list-disc list-inside">
            <li>Filter by date range, status, job type, matched-applicant thresholds{isHead && ', and recruiter email'}.</li>
            <li>Pick exactly which columns the CSV should contain.</li>
            <li>Preview the first 50 rows in-place; the CSV exports the full set.</li>
          </ul>
          <button onClick={() => setOpen(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-zinc-100" data-testid="open-jobs-report-btn">
            <Download className="w-4 h-4" /> Configure & preview
          </button>
        </div>
      </div>

      <ReportDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Jobs activity report"
        columns={columns}
        defaultSelected={defaultSelected}
        defaultFilters={defaultFilters}
        filterSchema={filterSchema}
        fetchPreview={(f, cs) => reportsAPI.recruiterPreview(f, cs)}
        downloadCsv={(f, cs) => reportsAPI.recruiterDownload(f, cs)}
      />
    </div>
  );
}
